import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { VIDEO_FEATURED, VIDEOS, VideoItem } from '../content';
import { RevealDirective } from '../reveal.directive';

/**
 * A video gallery: one big featured film and a row of smaller ones. Nothing
 * loads from YouTube until a card is played — then the film opens in a window
 * (no autoplay on page load, and the cookie-less youtube-nocookie player).
 */
@Component({
  selector: 'app-videos',
  imports: [RevealDirective],
  templateUrl: './videos.html',
  styleUrl: './videos.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'close()' },
})
export class Videos {
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly featured = VIDEO_FEATURED;
  protected readonly videos = VIDEOS;

  protected readonly playing = signal<VideoItem | null>(null);
  /** the player's address — built from our own video ids only, so it is safe to trust */
  protected readonly src = computed(() => {
    const video = this.playing();
    return video
      ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.id)}?autoplay=1&rel=0`)
      : null;
  });

  private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');
  /** the card that opened the window, so that focus can go back to it */
  private opener: HTMLElement | null = null;

  constructor() {
    // the window's close button takes focus as soon as the window is there
    effect(() => this.closeButton()?.nativeElement.focus());
  }

  protected play(video: VideoItem, event: Event): void {
    this.opener = event.currentTarget as HTMLElement;
    this.playing.set(video);
    document.body.style.overflow = 'hidden';
  }

  protected close(): void {
    if (!this.playing()) return;
    this.playing.set(null);
    document.body.style.overflow = '';
    this.opener?.focus();
  }
}
